import React, { useState, useEffect, useCallback, useRef } from "react";
import DashboardHeader from "../../layouts/dashboard_header";
import SideMenu from "../../layouts/dashboard_menu";
import DashboardFooter from "../../layouts/dashboard_footer";
import ServiceBreadcrumb from "../../layouts/components/ServiceBreadcrumb";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faGlobe, faEye, faSearch, faFilter, faSortUp, faSortDown, faSort,
    faHeartbeat, faStethoscope, faBrain, faTooth, faBaby, faMicroscope, 
    faDumbbell, faFemale, faProcedures, faAmbulance, faCalendarCheck, faHandshake
} from '@fortawesome/free-solid-svg-icons';
import { useBaseUrl } from "../../../utils/utils";
import { useNavigate } from "react-router-dom";

interface ServiceRequest {
    id: number;
    title: string;
    description: string;
    status: string;
    priority: string;
    engagement_type: string;
    patient_name?: string;
    created: string;
    updated: string;
    clinic_name?: string;
    start_date?: string;
    end_date?: string;
    request_type: 'open';
    creator: {
        id: number;
        name: string;
    };
    required_services?: string[];
    min_pay?: number;
    max_pay?: number;
    response_time?: number;
}

const GeneralRequests: React.FC = () => {
    const baseUrl = useBaseUrl();
    const navigate = useNavigate();
    const [requests, setRequests] = useState<ServiceRequest[]>([]);
    const [loading, setLoading] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [page, setPage] = useState(1);
    const [pageSize] = useState(20);
    
    // Filters and search
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [priorityFilter, setPriorityFilter] = useState('');
    const [sortField, setSortField] = useState<keyof ServiceRequest>('created');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards');

    // Infinite scroll ref
    const observer = useRef<IntersectionObserver | undefined>(undefined);
    const lastRequestRef = useRef<HTMLDivElement | null>(null);

    // Last request element callback for infinite scroll
    const lastRequestCallback = useCallback((node: HTMLDivElement) => {
        if (loading) return;
        if (observer.current) observer.current.disconnect();
        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && hasMore) {
                setPage(prevPage => prevPage + 1);
            }
        });
        if (node) observer.current.observe(node);
    }, [loading, hasMore]);

    useEffect(() => {
        document.title = "General Service Requests - Uzima Healthcare";
        fetchRequests(true); // Reset and fetch first page
    }, [searchTerm, statusFilter, priorityFilter, sortField, sortDirection]);

    useEffect(() => {
        if (page > 1) {
            fetchRequests(false); // Fetch additional pages
        }
    }, [page]);

    const fetchRequests = async (reset: boolean = false) => {
        try {
            setLoading(true);
            const currentPage = reset ? 1 : page;
            
            const params = new URLSearchParams({
                type: 'open',
                page: currentPage.toString(),
                page_size: pageSize.toString(),
                search: searchTerm,
                status: statusFilter,
                priority: priorityFilter,
                sort_by: sortField,
                sort_direction: sortDirection
            });

            const response = await fetch(`${baseUrl}collaborations/general-requests/?${params}`);
            if (response.ok) {
                const data = await response.json();
                const newRequests = data.data?.requests || [];
                
                if (reset) {
                    setRequests(newRequests);
                    setPage(1);
                } else {
                    setRequests(prev => [...prev, ...newRequests]);
                }
                
                setHasMore(newRequests.length === pageSize);
            } else {
                console.error('Failed to fetch general requests');
                if (reset) setRequests([]);
            }
        } catch (error) {
            console.error('Error fetching general requests:', error);
            if (reset) setRequests([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSort = (field: keyof ServiceRequest) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const clearFilters = () => {
        setSearchTerm('');
        setStatusFilter('');
        setPriorityFilter('');
        setSortField('created');
        setSortDirection('desc');
    };

    const handleApplyToRequest = async (requestId: number) => {
        try {
            // Navigate to application form with request ID
            navigate(`/institution/services/apply-to-request/${requestId}`);
        } catch (error) {
            console.error('Error applying to request:', error);
        }
    };

    const getRequestIcon = (title: string, engagementType: string) => {
        const lowerTitle = title.toLowerCase();
        
        if (lowerTitle.includes('cardio') || lowerTitle.includes('heart')) return faHeartbeat;
        if (lowerTitle.includes('consultation') || lowerTitle.includes('checkup')) return faStethoscope;
        if (lowerTitle.includes('neuro') || lowerTitle.includes('brain')) return faBrain;
        if (lowerTitle.includes('dental') || lowerTitle.includes('tooth')) return faTooth;
        if (lowerTitle.includes('pediatric') || lowerTitle.includes('child')) return faBaby;
        if (lowerTitle.includes('lab') || lowerTitle.includes('test')) return faMicroscope;
        if (lowerTitle.includes('physio') || lowerTitle.includes('therapy')) return faDumbbell;
        if (lowerTitle.includes('gyneco') || lowerTitle.includes('women')) return faFemale;
        if (lowerTitle.includes('surgery') || lowerTitle.includes('operation')) return faProcedures;
        if (lowerTitle.includes('emergency') || lowerTitle.includes('urgent')) return faAmbulance;
        if (lowerTitle.includes('appointment') || lowerTitle.includes('schedule')) return faCalendarCheck;
        
        return faStethoscope;
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case 'pending': return 'warning';
            case 'in_progress': return 'info';
            case 'completed': return 'success';
            case 'cancelled': return 'danger';
            case 'approved': return 'success';
            case 'rejected': return 'danger';
            default: return 'secondary';
        }
    };

    const getPriorityColor = (priority: string) => {
        switch (priority.toLowerCase()) {
            case 'low': return 'success';
            case 'normal': return 'info';
            case 'high': return 'warning';
            case 'urgent': return 'danger';
            default: return 'secondary';
        }
    };

    return (
        <>
            <DashboardHeader />
            <SideMenu />
            <div className={"vertical-overlay"} />
            <div className={"main-content"}>
                <div className={"page-content"}>
                    <div className={"container-fluid"}>
                        <ServiceBreadcrumb currentPage="General Service Requests" />
                        <div className="row mb-4 align-items-center">
                            <div className="col-8">
                                <h2 className="fw-bold mb-0">
                                    <FontAwesomeIcon icon={faGlobe} className="me-2 text-primary" />
                                    General Service Requests
                                </h2>
                                <div className="text-muted">Service requests sent to all qualified providers</div>
                            </div>
                            <div className="col-4 text-end">
                                <span className="badge bg-primary fs-6">
                                    {requests.length} requests
                                </span>
                            </div>
                        </div>

                        {/* Search and Filters */}
                        <div className="card mb-4">
                            <div className="card-body">
                                <div className="row g-3 align-items-end">
                                    <div className="col-md-4">
                                        <label className="form-label">Search</label>
                                        <div className="input-group">
                                            <span className="input-group-text">
                                                <FontAwesomeIcon icon={faSearch} />
                                            </span>
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Search requests..."
                                                value={searchTerm}
                                                onChange={(e) => setSearchTerm(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label">Status</label>
                                        <select
                                            className="form-select"
                                            value={statusFilter}
                                            onChange={(e) => setStatusFilter(e.target.value)}
                                        >
                                            <option value="">All Statuses</option>
                                            <option value="pending">Pending</option>
                                            <option value="in_progress">In Progress</option>
                                            <option value="completed">Completed</option>
                                            <option value="cancelled">Cancelled</option>
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <label className="form-label">Priority</label>
                                        <select
                                            className="form-select"
                                            value={priorityFilter}
                                            onChange={(e) => setPriorityFilter(e.target.value)}
                                        >
                                            <option value="">All Priorities</option>
                                            <option value="low">Low</option>
                                            <option value="normal">Normal</option>
                                            <option value="high">High</option>
                                            <option value="urgent">Urgent</option>
                                        </select>
                                    </div>
                                    <div className="col-md-2">
                                        <button
                                            className="btn btn-outline-secondary w-100"
                                            onClick={clearFilters}
                                        >
                                            <FontAwesomeIcon icon={faFilter} className="me-1" />
                                            Clear
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* View Mode Toggle */}
                        <div className="card mb-4">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h6 className="mb-0">View Mode</h6>
                                    <div className="btn-group btn-group-sm">
                                        <button 
                                            className={`btn btn-outline-primary ${viewMode === 'cards' ? 'active' : ''}`}
                                            onClick={() => setViewMode('cards')}
                                        >
                                            <FontAwesomeIcon icon={faGlobe} className="me-1" />
                                            Cards
                                        </button>
                                        <button 
                                            className={`btn btn-outline-primary ${viewMode === 'list' ? 'active' : ''}`}
                                            onClick={() => setViewMode('list')}
                                        >
                                            <FontAwesomeIcon icon={faSort} className="me-1" />
                                            Table
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Requests Grid */}
                        <div className={viewMode === 'cards' ? 'row g-4' : 'd-none'}>
                            {requests.map((request, index) => (
                                <div 
                                    key={request.id} 
                                    className="col-lg-6 col-xl-4"
                                    ref={index === requests.length - 1 ? lastRequestCallback : undefined}
                                >
                                    <div 
                                        className="card h-100 border-0 shadow-sm position-relative overflow-hidden"
                                        style={{ 
                                            cursor: 'pointer',
                                            transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                            background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)'
                                        }}
                                        onClick={() => navigate(`/institution/services/service-request-details/${request.id}`)}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = 'translateY(-5px)';
                                            e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.15)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = 'translateY(0)';
                                            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                                        }}
                                    >
                                        {/* Status Badge */}
                                        <div className="position-absolute top-0 end-0 p-3">
                                            <span className={`badge bg-${getStatusColor(request.status)} fs-6 px-3 py-2`}>
                                                {request.status}
                                            </span>
                                        </div>

                                        {/* Priority Badge */}
                                        <div className="position-absolute top-0 start-0 p-3">
                                            <span className={`badge bg-${getPriorityColor(request.priority)} fs-6 px-3 py-2`}>
                                                {request.priority}
                                            </span>
                                        </div>

                                        <div className="card-body p-4">
                                            {/* Icon and Title */}
                                            <div className="text-center mb-4">
                                                <div 
                                                    className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                                    style={{
                                                        width: '80px',
                                                        height: '80px',
                                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        color: 'white',
                                                        boxShadow: '0 8px 25px rgba(102, 126, 234, 0.3)'
                                                    }}
                                                >
                                                    <FontAwesomeIcon 
                                                        icon={getRequestIcon(request.title, request.engagement_type)} 
                                                        size="2x"
                                                    />
                                                </div>
                                                <h5 className="card-title fw-bold text-dark mb-2" style={{ fontSize: '1.1rem' }}>
                                                    {request.title}
                                                </h5>
                                            </div>

                                            {/* Description */}
                                            <p className="card-text text-muted mb-3" style={{ fontSize: '0.9rem', lineHeight: '1.5' }}>
                                                {request.description.length > 100 
                                                    ? `${request.description.substring(0, 100)}...` 
                                                    : request.description
                                                }
                                            </p>

                                            {/* Engagement Type */}
                                            <div className="text-center mb-3">
                                                <span className="badge bg-info fs-6 px-3 py-2">
                                                    {request.engagement_type}
                                                </span>
                                            </div>

                                            {/* Payment Range */}
                                            {request.min_pay && request.max_pay && (
                                                <div className="text-center mb-3">
                                                    <small className="text-muted d-block">Payment Range</small>
                                                    <div className="fw-bold text-success">
                                                        Ksh {request.min_pay.toLocaleString()} - {request.max_pay.toLocaleString()}
                                                    </div>
                                                </div>
                                            )}

                                            {/* Response Time */}
                                            {request.response_time && (
                                                <div className="text-center mb-3">
                                                    <small className="text-muted d-block">Response Time</small>
                                                    <div className="fw-bold text-info">
                                                        {request.response_time} hours
                                                    </div>
                                                </div>
                                            )}

                                            {/* Patient Info */}
                                            {request.patient_name && (
                                                <div className="text-center mb-3">
                                                    <small className="text-muted">
                                                        Patient: {request.patient_name}
                                                    </small>
                                                </div>
                                            )}

                                            {/* Creator Info */}
                                            {request.creator && (
                                                <div className="text-center mb-3">
                                                    <small className="text-muted">
                                                        By: {request.creator.name}
                                                    </small>
                                                </div>
                                            )}

                                                                                         {/* Dates */}
                                             <div className="row text-center mb-3">
                                                 <div className="col-6">
                                                     <small className="text-muted d-block">Created</small>
                                                     <small className="fw-bold text-dark">
                                                         {new Date(request.created).toLocaleDateString()}
                                                     </small>
                                                 </div>
                                                {request.start_date && (
                                                    <div className="col-6">
                                                        <small className="text-muted d-block">Start Date</small>
                                                        <small className="fw-bold text-dark">
                                                            {new Date(request.start_date).toLocaleDateString()}
                                                        </small>
                                                    </div>
                                                )}
                                            </div>

                                            {/* Action Buttons */}
                                            <div className="text-center">
                                                <div className="d-grid gap-2 mb-2">
                                                    <button 
                                                        className="btn btn-success btn-sm px-4 py-2"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleApplyToRequest(request.id);
                                                        }}
                                                        style={{
                                                            background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)',
                                                            border: 'none',
                                                            borderRadius: '25px',
                                                            boxShadow: '0 4px 15px rgba(76, 175, 80, 0.3)'
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faHandshake} className="me-2" />
                                                        Apply to Request
                                                    </button>
                                                </div>
                                                
                                                <button 
                                                    className="btn btn-primary btn-sm px-4 py-2"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/institution/services/service-request-details/${request.id}`);
                                                    }}
                                                    style={{
                                                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                        border: 'none',
                                                        borderRadius: '25px',
                                                        boxShadow: '0 4px 15px rgba(102, 126, 234, 0.3)'
                                                    }}
                                                >
                                                    <FontAwesomeIcon icon={faEye} className="me-2" />
                                                    View Details
                                                </button>
                                            </div>
                                        </div>

                                        {/* Decorative Elements */}
                                        <div 
                                            className="position-absolute bottom-0 start-0 w-100 h-100"
                                            style={{
                                                background: 'linear-gradient(135deg, transparent 0%, rgba(102, 126, 234, 0.05) 100%)',
                                                pointerEvents: 'none'
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Requests Table - List View */}
                        <div className={viewMode === 'list' ? 'd-block' : 'd-none'}>
                            <div className="card">
                                <div className="card-body">
                                    <div className="table-responsive">
                                        <table className="table table-hover">
                                            <thead>
                                                <tr>
                                                    <th>Title</th>
                                                    <th>Status</th>
                                                    <th>Priority</th>
                                                    <th>Engagement Type</th>
                                                    <th>Clinic</th>
                                                    <th>Pay Range</th>
                                                    <th>Start Date</th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {requests.map((request, index) => (
                                                    <tr key={request.id}>
                                                        <td>
                                                            <strong>{request.title}</strong>
                                                            <br />
                                                            <small className="text-muted">
                                                                {request.description.length > 50 
                                                                    ? `${request.description.substring(0, 50)}...` 
                                                                    : request.description
                                                                }
                                                            </small>
                                                        </td>
                                                        <td>
                                                            <span className={`badge bg-${getStatusColor(request.status)}`}>
                                                                {request.status.replace('_', ' ').toUpperCase()}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <span className={`badge bg-${getPriorityColor(request.priority)}`}>
                                                                {request.priority.toUpperCase()}
                                                            </span>
                                                        </td>
                                                        <td>{request.engagement_type.replace('_', ' ').toUpperCase()}</td>
                                                        <td>{request.clinic_name || 'N/A'}</td>
                                                        <td>
                                                            {request.min_pay && request.max_pay 
                                                                ? `$${request.min_pay} - $${request.max_pay}`
                                                                : 'N/A'
                                                            }
                                                        </td>
                                                        <td>
                                                            {request.start_date 
                                                                ? new Date(request.start_date).toLocaleDateString()
                                                                : 'N/A'
                                                            }
                                                        </td>
                                                        <td>
                                                            <div className="btn-group btn-group-sm">
                                                                <button 
                                                                    className="btn btn-outline-success"
                                                                    onClick={() => handleApplyToRequest(request.id)}
                                                                >
                                                                    <FontAwesomeIcon icon={faHandshake} className="me-1" />
                                                                    Apply
                                                                </button>
                                                                <button 
                                                                    className="btn btn-outline-primary"
                                                                    onClick={() => navigate(`/institution/services/service-request-details/${request.id}`)}
                                                                >
                                                                    <FontAwesomeIcon icon={faEye} className="me-1" />
                                                                    View
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Loading Indicator */}
                        {loading && (
                            <div className="text-center py-4">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <div className="mt-2 text-muted">Loading more requests...</div>
                            </div>
                        )}

                        {/* No More Data */}
                        {!hasMore && requests.length > 0 && (
                            <div className="text-center py-4">
                                <div className="text-muted">
                                    <FontAwesomeIcon icon={faGlobe} size="2x" className="mb-3" />
                                    <h6>No more requests to load</h6>
                                    <p>You've reached the end of the general service requests.</p>
                                </div>
                            </div>
                        )}

                        {/* Empty State */}
                        {!loading && requests.length === 0 && (
                            <div className="text-center py-5">
                                <FontAwesomeIcon icon={faGlobe} size="3x" className="mb-3 text-primary" />
                                <h5>No general service requests found</h5>
                                <p className="text-muted">No general service requests match your current filters.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <DashboardFooter />
        </>
    );
};

export default GeneralRequests;
